import React, { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    X,
    Trash2,
    Eye,
    Edit2,
    Search,
    PlusCircle,
} from "lucide-react";
import "./ListGenericSkills.css";
import {
    fetchCompetencesGeneriquesRequest,
    createCompetenceGeneriqueRequest,
    updateCompetenceGeneriqueRequest,
    deleteCompetenceGeneriqueRequest,
} from "../../../redux/actions/competencesGeneriquesActions";

/* ─────────────────────────────────────────
   DATA STATIQUE (Options)
 ───────────────────────────────────────── */
const CATEGORY_OPTIONS = [
    'Autonomie',
    'Autres qualités personnelles',
    'Engagement dans ses missions',
    'Fiabilité',
    "Investissement dans l'entreprise",
    'Orientation client',
    'Performance dans son poste',
    'Sens du collectif et coopération',
];

/* ─────────────────────────────────────────
   HELPERS
 ───────────────────────────────────────── */
const LEVEL_CONFIG = {
    1: { label: "Débutant",        },
    2: { label: "Intermédiaire",   },
    3: { label: "Avancé",          },
    4: { label: "Expert",          },
    5: { label: "Maître",          },
};

const LEVEL_OPTIONS = [
    { value: 1, label: "1 — Débutant" },
    { value: 2, label: "2 — Intermédiaire" },
    { value: 3, label: "3 — Avancé" },
    { value: 4, label: "4 — Expert" },
    { value: 5, label: "5 — Maître" },
];

const LevelBadge = ({ level }) => {
    const cfg = LEVEL_CONFIG[level] || { label: `Niveau ${level}`, bg: "#f3f4f6", color: "#374151" };
    return (
        <span className="gsBadge" style={{ background: cfg.bg, color: cfg.color }}>
      {cfg.label}
    </span>
    );
};

const EMPTY_FORM = { nom: "", reference: "", level: 1, categorie: "Soft Skills", description: "" };

/* ─────────────────────────────────────────
   MODAL GÉNÉRIQUE
 ───────────────────────────────────────── */
const Modal = ({ open, title, children, footer, onClose }) => {
    if (!open) return null;
    return (
        <div className="gsModal" onMouseDown={onClose}>
            <div className="gsModal__panel" onMouseDown={(e) => e.stopPropagation()}>
                <div className="gsModal__header">
                    <div className="gsModal__title">{title}</div>
                    <button className="gsIconBtn" onClick={onClose} type="button">
                        <X size={18} />
                    </button>
                </div>
                <div className="gsModal__body">{children}</div>
                {footer && <div className="gsModal__footer">{footer}</div>}
            </div>
        </div>
    );
};

/* ─────────────────────────────────────────
   COMPOSANT PRINCIPAL
 ───────────────────────────────────────── */
const PER_PAGE = 8;

export default function ListGenericSkills() {
    const dispatch = useDispatch();
    const { competencesGeneriques: skills, loading } = useSelector((s) => s.competencesGeneriques);

    /* ── state ── */
    const [filters, setFilters]         = useState({ nom: "", ref: "", level: "" });
    const [page, setPage]               = useState(1);

    const [showCreate, setShowCreate]   = useState(false);
    const [showEdit, setShowEdit]       = useState(false);
    const [showView, setShowView]       = useState(false);
    const [showDelete, setShowDelete]   = useState(false);

    const [selected, setSelected]       = useState(null);
    const [form, setForm]               = useState(EMPTY_FORM);
    const [formError, setFormError]     = useState("");

    useEffect(() => {
        dispatch(fetchCompetencesGeneriquesRequest());
    }, [dispatch]);

    /* ── filtrage + tri ── */
    const filtered = useMemo(() => {
        return (skills || [])
            .filter((s) => {
                if (filters.nom  && !s.nom.toLowerCase().includes(filters.nom.toLowerCase()))  return false;
                if (filters.ref  && !(s.reference || "").toLowerCase().includes(filters.ref.toLowerCase())) return false;
                if (filters.level && String(s.level) !== filters.level) return false;
                return true;
            })
            .sort((a, b) => a.nom.localeCompare(b.nom));
    }, [skills, filters]);

    /* ── pagination ── */
    const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
    const safePage   = Math.min(page, totalPages);
    const paginated  = filtered.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

    const handleFilterChange = (field, value) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
        setPage(1);
    };
    const resetFilters = () => { setFilters({ nom: "", ref: "", level: "" }); setPage(1); };
    const isFiltered   = filters.nom || filters.ref || filters.level;

    /* ── validation ── */
    const validate = (f) => {
        if (!f.nom.trim())          return "Le nom est obligatoire.";
        if (f.nom.trim().length < 2) return "Le nom doit contenir au moins 2 caractères.";
        return "";
    };

    /* ── create ── */
    const openCreate = () => { setForm(EMPTY_FORM); setFormError(""); setShowCreate(true); };
    const closeCreate = () => setShowCreate(false);
    const submitCreate = () => {
        const err = validate(form);
        if (err) { setFormError(err); return; }
        
        dispatch(createCompetenceGeneriqueRequest({
            nom: form.nom.trim(),
            reference: form.reference.trim() || null,
            level: parseInt(form.level),
            categorie: form.categorie || null,
            description: form.description.trim() || null
        }));
        
        closeCreate();
    };

    /* ── edit ── */
    const openEdit = (skill) => {
        setSelected(skill);
        setForm({ 
            nom: skill.nom || "", 
            reference: skill.reference || "", 
            level: skill.level || 1, 
            categorie: skill.categorie || "Soft Skills", 
            description: skill.description || "" 
        });
        setFormError("");
        setShowEdit(true);
    };
    const closeEdit = () => { setShowEdit(false); setSelected(null); };
    const submitEdit = () => {
        const err = validate(form);
        if (err) { setFormError(err); return; }
        
        dispatch(updateCompetenceGeneriqueRequest(selected.id, {
            nom: form.nom.trim(),
            reference: form.reference.trim() || null,
            level: parseInt(form.level),
            categorie: form.categorie || null,
            description: form.description.trim() || null
        }));
        
        closeEdit();
    };

    /* ── view ── */
    const openView   = (skill) => { setSelected(skill); setShowView(true); };
    const closeView  = () => { setShowView(false); setSelected(null); };

    /* ── delete ── */
    const openDelete   = (skill) => { setSelected(skill); setShowDelete(true); };
    const closeDelete  = () => { setShowDelete(false); setSelected(null); };
    const confirmDelete = () => {
        dispatch(deleteCompetenceGeneriqueRequest(selected.id));
        closeDelete();
    };

    /* ── pagination helpers ── */
    const pageNumbers = () => {
        const pages = [];
        for (let p = 1; p <= totalPages; p++) {
            if (p === 1 || p === totalPages || Math.abs(p - safePage) <= 1) pages.push(p);
            else if (Math.abs(p - safePage) === 2) pages.push("...");
        }
        return [...new Set(pages)];
    };

    /* ── form field helper ── */
    const renderField = (id, label, type = "text", placeholder = "", extra = {}) => (
        <div className="gsField">
            <label htmlFor={id}>{label}</label>
            {type === "textarea" ? (
                <textarea
                    id={id}
                    rows={3}
                    placeholder={placeholder}
                    value={form[id]}
                    onChange={(e) => setForm((f) => ({ ...f, [id]: e.target.value }))}
                />
            ) : type === "select" ? (
                <select
                    id={id}
                    value={form[id]}
                    onChange={(e) => setForm((f) => ({ ...f, [id]: e.target.value }))}
                >
                    {LEVEL_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </select>
            ) : (
                <input
                    id={id}
                    type={type}
                    placeholder={placeholder}
                    value={form[id]}
                    onChange={(e) => setForm((f) => ({ ...f, [id]: e.target.value }))}
                    {...extra}
                />
            )}
        </div>
    );

    const renderFormContent = () => (
        <div className="gsForm">
            <div className="gsForm2">
                {renderField("nom",  "Nom *",       "text", "Ex : Gestion de projet")}
                {renderField("reference",  "Référence",   "text", "Ex : GEN-001")}
            </div>
            {renderField("level", "Niveau *", "select")}
            <div className="gsField">
                <label htmlFor="categorie">Catégorie *</label>
                <select
                    id="categorie"
                    value={form.categorie}
                    onChange={(e) => setForm((f) => ({ ...f, categorie: e.target.value }))}
                >
                    {CATEGORY_OPTIONS.map((c) => (
                        <option key={c} value={c}>{c}</option>
                    ))}
                </select>
            </div>
            {renderField("description", "Description", "textarea", "Description de la compétence...")}
            {formError && <p className="gsFormError">{formError}</p>}
        </div>
    );

    /* ────────────────────────────────────────
       RENDER
    ─────────────────────────────────────── */
    return (
        <div className="gsPage">

            {/* ── HEADER ── */}
            <div className="gsHeader">
                <h1>Compétences génériques</h1>
                <p>Gérez les compétences génériques de votre organisation</p>
            </div>

            {/* ── FILTRES ── */}
            <div className="gsCard">
                <div className="gsBody">
                    <div className="gsFilterTitle">
                        <Search size={16} />
                        Filtre de recherche
                    </div>

                    <div className="gsFilterGrid">
                        <div className="gsField">
                            <label>Nom</label>
                            <input
                                type="text"
                                placeholder="Rechercher par nom..."
                                value={filters.nom}
                                onChange={(e) => handleFilterChange("nom", e.target.value)}
                            />
                        </div>
                        <div className="gsField">
                            <label>Référence</label>
                            <input
                                type="text"
                                placeholder="Rechercher par référence..."
                                value={filters.ref}
                                onChange={(e) => handleFilterChange("ref", e.target.value)}
                            />
                        </div>
                        <div className="gsField">
                            <label>Niveau</label>
                            <select value={filters.level} onChange={(e) => handleFilterChange("level", e.target.value)}>
                                <option value="">Tous les niveaux</option>
                                {LEVEL_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="gsFilterActions">
                        <button className="gsBtn gsBtn--primary" type="button" onClick={openCreate} disabled={loading}>
                            <PlusCircle size={16} />
                            Ajouter une compétence
                        </button>
                        <button
                            className={`gsBtn ${isFiltered ? "gsBtn--warning" : "gsBtn--ghost"}`}
                            type="button"
                            onClick={resetFilters}
                            disabled={!isFiltered}
                        >
                            Réinitialiser les filtres
                        </button>
                    </div>
                </div>
            </div>

            {/* ── LISTE ── */}
            <div className="gsCard">
                <div className="gsBody">
                    <div className="gsTopRow">
                        <h2 className="gsTitle">Liste des compétences ({filtered.length})</h2>
                    </div>

                    <div className="gsTableWrap">
                        <table className="gsTable">
                            <thead>
                            <tr>
                                <th>Nom</th>
                                <th>Référence</th>
                                <th>Catégorie</th>
                                <th>Niveau</th>
                                <th>Description</th>
                                <th className="r">Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            {paginated.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="gsEmptyCell">
                                        {loading ? "Chargement en cours..." : "Aucune compétence générique trouvée"}
                                    </td>
                                </tr>
                            ) : (
                                paginated.map((s) => (
                                    <tr key={s.id}>
                                        <td className="gsStrong">{s.nom}</td>
                                        <td className="gsMuted">{s.reference || " — "}</td>
                                        <td><span className="gsCatBadge">{s.categorie || " — "}</span></td>
                                        <td><LevelBadge level={s.level} /></td>
                                        <td className="gsMuted gsDescCell">{s.description || " — "}</td>
                                        <td className="r">
                                            <div className="gsRowActions">
                                                <button className="gsIconBtn" title="Voir" type="button" onClick={() => openView(s)}>
                                                    <Eye size={16} />
                                                </button>
                                                <button className="gsIconBtn" title="Modifier" type="button" onClick={() => openEdit(s)}>
                                                    <Edit2 size={16} />
                                                </button>
                                                <button className="gsIconBtn gsIconBtn--danger" title="Supprimer" type="button" onClick={() => openDelete(s)}>
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                            </tbody>
                        </table>
                    </div>

                    {/* ── PAGINATION ── */}
                    {totalPages > 1 && (
                        <div className="gsPagination">
                            <button className="gsPageBtn" disabled={safePage === 1} onClick={() => setPage(safePage - 1)}>‹</button>
                            {pageNumbers().map((p, i) =>
                                p === "..." ? (
                                    <span key={`dots-${i}`} className="gsPageDots">…</span>
                                ) : (
                                    <button
                                        key={p}
                                        className={`gsPageBtn${p === safePage ? " active" : ""}`}
                                        onClick={() => setPage(p)}
                                    >
                                        {p}
                                    </button>
                                )
                            )}
                            <button className="gsPageBtn" disabled={safePage === totalPages} onClick={() => setPage(safePage + 1)}>›</button>
                        </div>
                    )}
                </div>
            </div>

            {/* ── MODAL CRÉATION ── */}
            <Modal
                open={showCreate}
                title="Nouvelle compétence générique"
                onClose={closeCreate}
                footer={
                    <>
                        <button className="gsBtn gsBtn--ghost" type="button" onClick={closeCreate}>Annuler</button>
                        <button className="gsBtn gsBtn--success" type="button" onClick={submitCreate}>Enregistrer</button>
                    </>
                }
            >
                {renderFormContent()}
            </Modal>

            {/* ── MODAL MODIFICATION ── */}
            <Modal
                open={showEdit}
                title={`Modifier — ${selected?.nom || ""}`}
                onClose={closeEdit}
                footer={
                    <>
                        <button className="gsBtn gsBtn--ghost" type="button" onClick={closeEdit}>Annuler</button>
                        <button className="gsBtn gsBtn--success" type="button" onClick={submitEdit}>Enregistrer</button>
                    </>
                }
            >
                {renderFormContent()}
            </Modal>

            {/* ── MODAL VUE ── */}
            <Modal
                open={showView}
                title={`Détails — ${selected?.nom || ""}`}
                onClose={closeView}
                footer={
                    <button className="gsBtn gsBtn--primary" type="button" onClick={closeView}>Fermer</button>
                }
            >
                {selected && (
                    <div className="gsViewContent">
                        {[
                            ["Nom",         <span style={{ fontWeight: 500 }}>{selected.nom}</span>],
                            ["Référence",   selected.reference || " — "],
                            ["Catégorie",   selected.categorie || " — "],
                            ["Niveau",      <LevelBadge level={selected.level} />],
                            ["Description", selected.description || " — "],
                        ].map(([lbl, val]) => (
                            <div className="gsViewRow" key={lbl}>
                                <div className="gsViewLabel">{lbl}</div>
                                <div className="gsViewValue">{val}</div>
                            </div>
                        ))}
                    </div>
                )}
            </Modal>

            {/* ── MODAL SUPPRESSION ── */}
            <Modal
                open={showDelete}
                title="Confirmation de suppression"
                onClose={closeDelete}
                footer={
                    <>
                        <button className="gsBtn gsBtn--ghost" type="button" onClick={closeDelete}>Annuler</button>
                        <button className="gsBtn gsBtn--danger" type="button" onClick={confirmDelete}>Supprimer</button>
                    </>
                }
            >
                <div className="gsDeleteContent">
                    <p>
                        Voulez-vous vraiment supprimer la compétence{" "}
                        <strong>{selected?.nom}</strong> ?
                    </p>
                    <p className="gsWarning">Cette action est irréversible.</p>
                </div>
            </Modal>
        </div>
    );
}